'use client';

import { PromptExamples } from '@/types/terminal';
import { twMerge } from 'tailwind-merge';
import { ArrowRightIcon } from '@/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

export const Suggestions = ({
  suggestions,
  onSelect,
  isWidget,
}: {
  suggestions?: PromptExamples;
  onSelect: (value: string) => void;
  isWidget?: boolean;
}) => {
  const [showRightGradient, setShowRightGradient] = useState(true);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollWidth, scrollLeft, clientWidth } = scrollContainerRef.current;
    const isAtEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) <= 1;
    const isAtStart = scrollLeft <= 1;

    setShowRightGradient(!isAtEnd);
    setShowLeftGradient(!isAtStart);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  if (!suggestions) return null;

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className="flex gap-x-4 overflow-x-auto py-3 scrollbar-hide">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.value}
            role="button"
            onClick={() => onSelect(suggestion.value)}
            className={twMerge(
              'rounded-lg border border-grey-450 shrink-0 overflow-hidden',
              'hover:border-transparent hover:bg-gradient-to-r hover:from-yellow-500 hover:to-sky-600 hover:border-l-yellow-500 hover:border-r-sky-600'
            )}
          >
            <div
              className={twMerge(
                'flex gap-x-2 items-center justify-between px-3 py-2 text-grey-150 min-w-[218px] text-xs font-sans',
                isWidget ? 'bg-grey-800' : 'bg-neutral-950'
              )}
            >
              {suggestion.label}
              <ArrowRightIcon className="w-4 h-4" />
            </div>
          </div>
        ))}
      </div>
      {showLeftGradient && (
        <div
          className={twMerge(
            'absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-neutral-950 to-transparent pointer-events-none',
            isWidget ? 'from-grey-800' : 'from-neutral-950'
          )}
        />
      )}
      {showRightGradient && (
        <div
          className={twMerge(
            'absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-neutral-950 to-transparent pointer-events-none',
            isWidget ? 'from-grey-800' : 'from-neutral-950'
          )}
        />
      )}
    </div>
  );
};
